import { useState } from "react";
import api from "../lib/api";
import { MANUAL_LOOKUP_LINKS, SONGVIEW_LINKS, MISSING_SECTION } from "../lib/statuses";
import { toast } from "sonner";

export default function VerifyPanel({ track, onTrackChange, onJump }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isrcLoading, setIsrcLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/verify/${track.id}`);
      setResult(r.data);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else {
        toast.error(typeof det === "string" ? det : "Verification failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const runIsrcFill = async () => {
    setIsrcLoading(true);
    try {
      const r = await api.post(`/isrc/fill/${track.id}`);
      const status = r.data?.result?.status;
      if (status === "filled") toast.success(`ISRC found: ${r.data.track.isrc} — confirm it in Metadata`);
      else if (status === "needs_review") toast(`Several matches — saved ${r.data.track.isrc}, flagged for review`, { duration: 4000 });
      else if (status === "skipped") toast(`Track already has an ISRC`);
      else toast(`No confident Deezer match found`, { duration: 4000 });
      if (r.data?.track && onTrackChange) onTrackChange(r.data.track);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else {
        toast.error(typeof det === "string" ? det : "ISRC fill failed");
      }
    } finally {
      setIsrcLoading(false);
    }
  };

  const songview = SONGVIEW_LINKS(track);
  const manual = MANUAL_LOOKUP_LINKS(track);

  return (
    <div style={{ padding: "20px 0", animation: "fadeIn 0.3s ease" }} data-testid="verify-panel">
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Verify · {track.title}</div>

      {/* Scorecard */}
      {result?.scorecard && (
        <div style={{ background: "var(--bg-elev-2)", padding: 18, borderRadius: 4, marginTop: 14, border: "1px solid var(--border-soft)" }}>
          <div className="serif" style={{ fontSize: 22, lineHeight: 1.1 }} data-testid="completeness-score">
            Registration completeness · {result.scorecard.completeness}%
          </div>
          <div className="usage-bar" style={{ marginTop: 12, marginBottom: 12 }}>
            <div className="usage-fill" style={{ width: `${result.scorecard.completeness}%`, background: result.scorecard.completeness === 100 ? "var(--green-light)" : undefined }} />
          </div>
          {result.scorecard.breakdown && (
            <div className="data-line">
              Core {result.scorecard.breakdown.core}/20 · Metadata {result.scorecard.breakdown.metadata}/30 · Credits {result.scorecard.breakdown.credits}/15 · Registrations {result.scorecard.breakdown.registrations}/25 · Verification {result.scorecard.breakdown.verification}/10
            </div>
          )}
        </div>
      )}

      <MissingList missing={result?.scorecard?.missing || track.completeness_breakdown?.missing || []} onJump={onJump} />

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button
          className="btn btn-primary"
          onClick={run}
          disabled={loading}
          style={{ flex: 1 }}
          data-testid="run-verify-btn"
        >
          {loading ? "Running cross-references…" : (result ? "Re-run Verification" : "Run Auto-Verification")}
        </button>
        <button
          className="btn btn-light"
          onClick={runIsrcFill}
          disabled={isrcLoading || !!track.isrc}
          title={track.isrc ? "Track already has an ISRC" : "Lookup ISRC via Deezer"}
          data-testid="fill-isrc-btn"
        >
          {isrcLoading ? "Looking up…" : "Fill ISRC"}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 18 }}>
          <Row ok={result.itunes?.matched} label={`iTunes Store · ${result.itunes?.count || 0} result${(result.itunes?.count || 0) === 1 ? "" : "s"}`} />
        </div>
      )}

      {/* R6 — Songview lookup */}
      <div style={{ marginTop: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>— Songview Lookup</div>
        <div style={{ color: "var(--text-mute)", fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
          Opens reconciled ASCAP/BMI ownership data for this work — songwriters, publishers, splits, ISWC, and IPI codes.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 6 }}>
          {songview.map((l) => (
            <a
              key={l.label}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="connection-btn"
              style={{
                textAlign: "center", textDecoration: "none",
                borderColor: l.primary ? "var(--accent)" : undefined,
                color: l.primary ? "var(--accent)" : undefined,
              }}
              data-testid={`songview-${l.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            >
              {l.label} ↗
            </a>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>— Manual Lookup Portals</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6 }}>
          {manual.map((l) => (
            <a
              key={l.label}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="connection-btn"
              style={{ textAlign: "center", textDecoration: "none" }}
              data-testid={`lookup-${l.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {l.label} →
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ ok, label }) {
  return (
    <div className="verify-row">
      <span className={`verify-mark ${ok ? "ok" : "no"}`}>{ok ? "✓" : "✕"}</span>
      {label}
    </div>
  );
}

function MissingList({ missing, onJump }) {
  if (!missing.length) return null;
  return (
    <div style={{ marginTop: 14 }} data-testid="whats-missing">
      <div className="eyebrow" style={{ marginBottom: 4 }}>— What&apos;s Missing</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
        {missing.map((m) => {
          const section = MISSING_SECTION[m];
          return (
            <button
              key={m}
              type="button"
              className={`missing-chip ${m === "ISRC_NEEDS_REVIEW" ? "review" : ""}`}
              disabled={!section}
              onClick={() => section && onJump?.(section)}
              title={section ? `Go to ${section}` : "Run auto-verification"}
              data-testid={`missing-${m.toLowerCase()}`}
            >
              {m === "ISRC_NEEDS_REVIEW" ? "ISRC: NEEDS REVIEW" : m.replace(/_/g, " ")}{section ? " ↑" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}
